import React, { useRef, useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormRow } from 'src/components/Form';
import { ButtonS } from 'src/components/Button';
import {
  useCreateReportReviewNote,
  useUpdateReportReviewNote,
  useDeleteReportReviewNote,
  useCreateReportReviewComment,
  useUpdateReportReviewComment,
  useDeleteReportReviewComment,
  useUpdateReviewNoteStatus,
} from 'src/hooks/reportReview/';
import { toSimpleDateStringForReportReview } from 'src/utils/format/date';
import { notification } from 'src/components/Notification';
import { useTranslation } from 'react-i18next';
import Checkbox from 'src/components/Checkbox';
import { withJobContext } from 'src/utils/context/JobContext';
import {
  UNDER_EXTERNAL_SAQ,
  READY_FOR_INTERNAL_SAQ,
  UNDER_INTERNAL_SAQ,
} from 'src/utils/data/job';
import TrixEditor from "./TrixEditor";
import s from './ReportReviewBlock.styles';
import 'src/styles/TrixEditor.css';

const ReportReviewBlock = ({
  jobContext,
  activeReportReviewNote,
  isCreateNote,
  showNote,
  activeReportReviewComments,
  refetchReportReview,
  jobId,
  type,
  typeId,
  typeVersionId,
  typeQuestionId,
  setIsCreateNote,
  isUnderReviewing,
  readonly,
  setShowResult,
  setShowNote,
  reportReviewDataLoading,
  userIsAuthorizedExternalSaqUser
}) => {
  const { t } = useTranslation();

  const {
    job,
    user: {
      isAuditor,
      isReportReviewer,
      isInternalUser,
    },
    hasSaqService
  } = jobContext;
  const [newNote, setNewNote] = useState({innerText: '' , innerHTML: ''});
  const [editNote, setEditNote] = useState({innerText: '' , innerHTML: ''});
  const [newComment, setNewComment] = useState({innerText: '' , innerHTML: ''});
  const [editComment, setEditComment] = useState({innerText: '' , innerHTML: ''});
  const [editorsVisible, setEditorsVisisble] = useState(true);

  useEffect(()=>{
    if(readonly && !isInternalUser && !userIsAuthorizedExternalSaqUser && hasSaqService){
      setEditorsVisisble(false);
    }
  },[readonly, userIsAuthorizedExternalSaqUser, hasSaqService]);

  const isExternalCheckboxVisible = [
    UNDER_EXTERNAL_SAQ,
    UNDER_INTERNAL_SAQ,
  ].includes(job?.status);

  const [isExternal, setIsExternal] = useState(false);
  useEffect(() => {
    if (activeReportReviewNote?.isExternal !== undefined) {
      setIsExternal(activeReportReviewNote?.isExternal);
    } else {
      setIsExternal([
        READY_FOR_INTERNAL_SAQ,
        UNDER_INTERNAL_SAQ,
      ].includes(job?.status));
    }
  }, [activeReportReviewNote?.isExternal, job?.status, typeQuestionId]);

  const [toggleBtn, setToogleBtn] = useState(false);
  const useCreateReportReviewCommentHook = useCreateReportReviewComment();
  const useUpdateReportReviewCommentHook = useUpdateReportReviewComment();
  const useDeleteReportReviewCommentHook = useDeleteReportReviewComment();
  const useCreateReportReviewNoteHook = useCreateReportReviewNote();
  const useUpdateReportReviewNoteHook = useUpdateReportReviewNote();
  const useDeleteReportReviewNoteHook = useDeleteReportReviewNote();
  const useUpdateReviewNoteStatusHook = useUpdateReviewNoteStatus();

  const [isCreatingNoteByAPI, setIsCreatingNoteByAPI] = useState(false);
  const [isUpdatingNoteByAPI, setIsUpdatingNoteByAPI] = useState(false);
  const [isCreatingCommentByAPI, setIsCreatingCommentByAPI] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingNote, setEditingNote] = useState(false);

  const createNote = async () => {
    let workSkipped = false;
    try {
      const { innerText, innerHTML } = newNote;
      
      if (!isCreatingNoteByAPI) {
        if (!innerText) {
          throw new Error('Note required');
        }
        setIsCreatingNoteByAPI(true);
        await useCreateReportReviewNoteHook({
          jobId,
          type,
          typeId,
          typeVersionId,
          typeQuestionId,
          note: innerText,
          noteHtml: innerHTML,
          isExternal,
        });
        await refetchReportReview();
        setIsCreateNote(false);
        setIsCreatingNoteByAPI(false);
        setShowNote(true);
        setShowResult(false);
        setNewNote({ innerText: '' , innerHTML: '' });
      } else {
        workSkipped = true;
      }
    } catch (e) {
      notification.error(e);
    } finally {
      if (!workSkipped) {
        setIsCreatingNoteByAPI(false);
      }
    }
  };

  const updateNote = async () => {
    try {
      const { innerText, innerHTML } = editNote;

      if (!isCreatingNoteByAPI) {
        if (!innerText) {
          throw new Error('Note required');
        }
        setIsCreatingNoteByAPI(true);
        await useUpdateReportReviewNoteHook({
          id: activeReportReviewNote.id,
          note: innerText,
          noteHtml: innerHTML,
          isExternal,
        });
        await refetchReportReview();
        setEditingNote(false);
        setIsCreateNote(false);
        setIsCreatingNoteByAPI(false);
        setShowNote(true);
        setShowResult(false);
        setEditNote({ innerText: '' , innerHTML: '' });
      }
    } catch (e) {
      notification.error(e);
    } finally {
      setIsCreatingNoteByAPI(false);
    }
  };

  const closeNote = async close => {
    if (isUpdatingNoteByAPI) {
      return null;
    }
    const reportReviewNoteId = activeReportReviewNote.id;

    setIsUpdatingNoteByAPI(true);

    await useUpdateReviewNoteStatusHook({
      id: reportReviewNoteId,
      close,
    });

    setIsUpdatingNoteByAPI(false);
    await refetchReportReview();
    return true;
  };

  const createComment = async () => {
    const { innerText, innerHTML } = newComment;

    if (!innerText) {
      notification.error('Comment required');
      return null;
    }
    if (isCreatingCommentByAPI) {
      return null;
    }
    const reportReviewNoteId = activeReportReviewNote.id;
    setIsCreatingCommentByAPI(true);
    await useCreateReportReviewCommentHook({
      reportReviewNoteId,
      comment: innerText,
      commentHtml: innerHTML,
    });
    await refetchReportReview();
    setIsCreatingCommentByAPI(false);
    setNewComment({innerText: '', innerHTML: ''});
    return true;
  };

  const updateComment = async () => {
    const { innerText, innerHTML } = editComment;

    if (!innerText) {
      notification.error('Comment required');
      return null;
    }
    if (isCreatingCommentByAPI) {
      return null;
    }
    setIsCreatingCommentByAPI(true);
    await useUpdateReportReviewCommentHook({
      id: editingCommentId,
      comment: innerText,
      commentHtml: innerHTML,
    });
    await refetchReportReview();
    setEditComment({innerText: '', innerHTML: ''});
    setIsCreatingCommentByAPI(false);
    setEditingCommentId(null);
    return true;
  };

  const deleteComment = async (id) => {
    if (isCreatingCommentByAPI) {
      return null;
    }
    setIsCreatingCommentByAPI(true);
    await useDeleteReportReviewCommentHook({
      id,
    });
    await refetchReportReview();
    setIsCreatingCommentByAPI(false);
    setEditingCommentId(null);
    return true;
  };

  const deleteNote = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();

    if (isCreatingCommentByAPI) {
      return null;
    }
    setIsCreatingCommentByAPI(true);
    await useDeleteReportReviewNoteHook({
      id,
    });
    await refetchReportReview();
    setIsCreatingCommentByAPI(false);
    setEditingNote(false);
    return true;
  };

  document.addEventListener('trix-file-accept', (event) => {
    event.preventDefault();
  });

  const activeReportReviewNoteIsVisible = activeReportReviewNote?.isExternal || isInternalUser;

  return (
    <Fragment>
      {activeReportReviewNote && activeReportReviewNoteIsVisible && showNote && (
        <s.ReportReviewBlock>
          {!editingNote && (<s.ReportReviewNoteInfoBox>
            <s.ReportReviewNoteBoxNoteInfoLeft>
              <s.ReportReviewNoteBoxNoteInfoLeftIn>
                {`${activeReportReviewNote.user.lastName} ${activeReportReviewNote.user.firstName}`}
              </s.ReportReviewNoteBoxNoteInfoLeftIn>
              <s.ReportReviewNoteBoxNoteInfoLeftIn>
                {(() => {
                  if ((isUnderReviewing && isReportReviewer) || (isAuditor && isExternalCheckboxVisible)) {
                    return activeReportReviewNote.closedAt ? (
                      <Fragment>
                        <s.ClosedSpan
                          onClick={() => setToogleBtn(!toggleBtn)}
                          pointer
                        >
                          {t('CLOSED')}
                          <s.ToggleSvg name="expand" />
                        </s.ClosedSpan>
                        {toggleBtn && (
                          <s.ToggleTransBtn
                            onClick={() => {
                              closeNote(false);
                              setToogleBtn(false);
                            }}
                          >
                            <s.ToggleTransOptionBlock>
                              {t('MOVE_TO')} <s.ClosedSpan open>{t('OPEN')}</s.ClosedSpan>
                            </s.ToggleTransOptionBlock>
                          </s.ToggleTransBtn>
                        )}
                      </Fragment>
                    ) : (
                      <Fragment>
                        <s.ClosedSpan
                          onClick={() => setToogleBtn(!toggleBtn)}
                          open
                          pointer
                        >
                          {t('OPEN')}
                          <s.ToggleSvg name="expand" />
                        </s.ClosedSpan>
                        {toggleBtn && (
                          <s.ToggleTransBtn
                            onClick={() => {
                              closeNote(true);
                              setToogleBtn(false);
                            }}
                          >
                            <s.ToggleTransOptionBlock open-to-close>
                              {t('MOVE_TO')} <s.ClosedSpan>{t('CLOSED')}</s.ClosedSpan>
                            </s.ToggleTransOptionBlock>
                          </s.ToggleTransBtn>
                        )}
                      </Fragment>
                    );
                  }
                  if (!isUnderReviewing || !isReportReviewer) {
                    return activeReportReviewNote.closedAt ? (
                      <Fragment>
                        <s.ClosedSpan>{t('CLOSED')}</s.ClosedSpan>
                      </Fragment>
                    ) : (
                      <Fragment>
                        <s.ClosedSpan open>{t('OPEN')}</s.ClosedSpan>
                      </Fragment>
                    );
                  }
                  return null;
                })()}
                <s.ClosedSpan disabled={!activeReportReviewNote.editable}
                              editDelete
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); if(!activeReportReviewNote.editable){ return null; }; setEditingNote(true); return null; }}>
                                {t('EDIT')}
                </s.ClosedSpan>
                <s.ClosedSpan disabled={!activeReportReviewNote.editable}
                              editDelete
                              onClick={(e) => { deleteNote(e, activeReportReviewNote.id); return null; }}>
                                {t('DELETE')}
                </s.ClosedSpan>
              </s.ReportReviewNoteBoxNoteInfoLeftIn>
            </s.ReportReviewNoteBoxNoteInfoLeft>
            <s.ReportReviewBlockTimeWrapperDiv>
              {isInternalUser && isExternalCheckboxVisible && (
              <s.ReportReviewBlockNoteTimeLabel>
                {t('NOTE_TYPE')}:
              </s.ReportReviewBlockNoteTimeLabel>
              )}
              <s.ReportReviewBlockNoteTimeLabel>
                {t('OPENED')}:
              </s.ReportReviewBlockNoteTimeLabel>
              <s.ReportReviewBlockNoteTimeLabel>
                {t('CLOSED')}:
              </s.ReportReviewBlockNoteTimeLabel>
            </s.ReportReviewBlockTimeWrapperDiv>
            <div>
              <s.ReportReviewBlockNoteTimeBox>
                <s.ReportReviewBlockNoteTime>
                {isInternalUser && isExternalCheckboxVisible && (
                  <s.ReportReviewBlockNoteTimeText>
                    {activeReportReviewNote.isExternal
                      ? t('EXTERNAL'): t('INTERNAL')}
                  </s.ReportReviewBlockNoteTimeText>
                  )}
                </s.ReportReviewBlockNoteTime>
                <s.ReportReviewBlockNoteTime>
                  <s.ReportReviewBlockNoteTimeText>
                    {toSimpleDateStringForReportReview(
                      activeReportReviewNote.createdAt,
                    )}
                  </s.ReportReviewBlockNoteTimeText>
                </s.ReportReviewBlockNoteTime>
                <s.ReportReviewBlockNoteTime>
                  <s.ReportReviewBlockNoteTimeText>
                    {activeReportReviewNote.closedAt
                      ? toSimpleDateStringForReportReview(
                        activeReportReviewNote.closedAt,
                      )
                      : t('N_A')}
                  </s.ReportReviewBlockNoteTimeText>
                </s.ReportReviewBlockNoteTime>
              </s.ReportReviewBlockNoteTimeBox>
            </div>
          </s.ReportReviewNoteInfoBox>)
          }
          {editorsVisible && editingNote && (<s.UpdateNoteTrixDiv>
            {editorsVisible && <FormRow>
              <s.ReportReviewNoteTextAreaWrapper>
                <TrixEditor
                  placeholder={t('ADD_YOUR_NOTE')}
                  value={activeReportReviewNote.noteHtml}
                  onChange={(val) => setEditNote(val)}
                  id="edit-note"
                />
              </s.ReportReviewNoteTextAreaWrapper>
            </FormRow>
              }
            {isExternalCheckboxVisible && (
              <FormRow>
                <Checkbox
                  id="isExternal"
                  label={t('REPORT_REVIEW_NOTE_IS_EXTERNAL')}
                  onChange={e => setIsExternal(e.target.checked)}
                  value={isExternal}
                />
                <s.ReportReviewNoteIsExternalComment dangerouslySetInnerHTML={{ __html: t('REPORT_REVIEW_NOTE_IS_EXTERNAL_COMMENT') }} />
              </FormRow>
            )}
            <FormRow style={{ position: 'relative', marginTop: '10px', marginBottom: '10px', }}>
              <ButtonS forUpdateNote onClick={updateNote} orange sm solid>
                {t('SEND')}
              </ButtonS>

              <ButtonS close forUpdateNote onClick={() => setEditingNote(false)} sm>
                CANCEL
              </ButtonS>
            </FormRow>
          </s.UpdateNoteTrixDiv>)
          }
          {!editingNote && (
            <s.ReportReviewNoteText
              className="trix-content"
              dangerouslySetInnerHTML={{
                __html: activeReportReviewNote.noteHtml || activeReportReviewNote.note,
              }}
            />
          )
          }          {(isUnderReviewing ||
            (!isUnderReviewing && activeReportReviewComments.length !== 0)) && (
            <s.ReportReviewNoteLine />
          )}

          {
            <div>
              {activeReportReviewComments.map((comment, index) => {
                const isLastComment =
                  activeReportReviewComments[
                    activeReportReviewComments.length - 1
                  ].id === comment.id;
                if(comment.id === editingCommentId && editorsVisible) {
                  return <s.ReportReviewAddingCommentBlock key={index}>
                    <TrixEditor
                      id="edit-comment"
                      placeholder={t('ADD_YOUR_NOTE')}
                      value={editComment.innerHTML}
                      onChange={(val) => setEditComment(val)}
                    />
                    <s.ReportReviewAddingCommentButtons>
                      <ButtonS onClick={updateComment} orange sm solid>
                        {t('SEND')}
                      </ButtonS>

                      <ButtonS ghost onClick={() => setEditingCommentId(null)} red sm>
                        {t('CANCEL')}
                      </ButtonS>
                    </s.ReportReviewAddingCommentButtons>
                  </s.ReportReviewAddingCommentBlock>;
                }
                return (
                  <Fragment key={comment.id}>
                    <s.ReportReviewCommentBox key={comment.id}>
                      <s.ReportReviewCommentInfoBox>
                        <s.ReportReviewCommentBoxInfoLeft>
                          <s.ReportReviewCommentBoxInfoLeftIn>
                            {`${comment.user.lastName} ${comment.user.firstName}`} <s.ClosedSpan disabled={editingCommentId || !comment.editable} editDelete leftMargin onClick={(e) => { e.preventDefault(); e.stopPropagation(); if(!comment.editable) { return null; }; setEditingCommentId(comment.id); setEditComment({innerText: comment.comment, innerHTML: comment.commentHtml}); return null; }}>{t('EDIT')}</s.ClosedSpan> <s.ClosedSpan disabled={editingCommentId || !comment.editable} editDelete leftMargin onClick={(e) => { e.preventDefault(); e.stopPropagation(); if(!comment.editable) { return null; }; deleteComment(comment.id); return null; }}>{t('DELETE')}</s.ClosedSpan>

                          </s.ReportReviewCommentBoxInfoLeftIn>
                        </s.ReportReviewCommentBoxInfoLeft>
                        <s.ReportReviewBlockNoteTimeBox>
                          <s.ReportReviewBlockNoteTime>
                            <s.ReportReviewBlockNoteTimeText>
                              {toSimpleDateStringForReportReview(
                                comment.createdAt,
                              )}
                            </s.ReportReviewBlockNoteTimeText>
                          </s.ReportReviewBlockNoteTime>
                        </s.ReportReviewBlockNoteTimeBox>
                      </s.ReportReviewCommentInfoBox>
                      <s.ReportReviewCommentText
                        className="trix-content"
                        dangerouslySetInnerHTML={{
                          __html: comment.commentHtml || comment.comment,
                        }}
                        isLastComment={isUnderReviewing && isLastComment}
                      />
                    </s.ReportReviewCommentBox>
                    {!isLastComment && <s.ReportReviewCommentLine />}
                  </Fragment>
                );
              })}
            </div>
          }
          {(
            isUnderReviewing
              ||
            (!isInternalUser && job?.status === UNDER_EXTERNAL_SAQ)
              ||
            (isAuditor && isExternalCheckboxVisible)
          ) && editorsVisible &&(
            <s.ReportReviewAddingCommentBlock>
              <TrixEditor
                id="new-comment"
                placeholder={t('ADD_YOUR_NOTE')}
                value={newComment.innerHTML}
                onChange={(val) => setNewComment(val)}
              />
              <s.ReportReviewAddingCommentButtons>
                <ButtonS onClick={createComment} orange sm solid>
                  {t('SEND')}
                </ButtonS>

                {!activeReportReviewNote.closedAt && isReportReviewer && (
                  <ButtonS onClick={() => closeNote(true)} orange sm solid>
                    {t('CLOSE_NOTE')}
                  </ButtonS>
                )}
              </s.ReportReviewAddingCommentButtons>
            </s.ReportReviewAddingCommentBlock>
          )}
        </s.ReportReviewBlock>
      )}
      {isCreateNote && (
        <Fragment>
          <FormRow>
            <s.NewNoteTitleText>{t('NEW_NOTE')}</s.NewNoteTitleText>
            <s.ReportReviewNoteTextAreaWrapper>
              <TrixEditor 
                onChange={(val) => setNewNote(val)}
                id="new-note"
                placeholder={t('ADD_YOUR_NOTE')}
              />
            </s.ReportReviewNoteTextAreaWrapper>
          </FormRow>
          {isExternalCheckboxVisible && (
            <FormRow>
              <Checkbox
                id="isExternal"
                label={t('REPORT_REVIEW_NOTE_IS_EXTERNAL')}
                onChange={e => setIsExternal(e.target.checked)}
                value={isExternal}
              />
              <s.ReportReviewNoteIsExternalComment dangerouslySetInnerHTML={{ __html: t('REPORT_REVIEW_NOTE_IS_EXTERNAL_COMMENT') }} />
            </FormRow>
          )}
          <FormRow style={{ position: 'relative', }}>
            <ButtonS leftMost onClick={createNote} orange sm solid>
              {t('SEND')}
            </ButtonS>
          </FormRow>
        </Fragment>
      )}
    </Fragment>
  );
};

ReportReviewBlock.propTypes = {
  activeReportReviewComments: PropTypes.arrayOf(PropTypes.object),
  activeReportReviewNote: PropTypes.object,
  isCreateNote: PropTypes.bool,
  isUnderReviewing: PropTypes.bool,
  jobId: PropTypes.number,
  refetchReportReview: PropTypes.func,
  reportReviewStatistics: PropTypes.arrayOf(PropTypes.object),
  setIsCreateNote: PropTypes.func,
  setShowNote: PropTypes.func,
  setShowResult: PropTypes.func,
  showNote: PropTypes.bool,
  readonly: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  type: PropTypes.string,
  typeId: PropTypes.number,
  typeQuestionId: PropTypes.number,
  typeVersionId: PropTypes.number,
  userIsAuthorizedExternalSaqUser: PropTypes.bool,
};

ReportReviewBlock.defaultProps = {
  readonly: false
};

export default React.memo(withJobContext(ReportReviewBlock));
